import { Camera, CameraOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { decodeFrame, nativeQrDetector } from "./qr";

const SCAN_EVERY_MS = 200;

type CameraState = "off" | "starting" | "on" | "unavailable";

/**
 * The tablet's camera reads the QR code on the customer's phone (T07-29). Opened with a tap
 * (browsers ask for the camera after a user gesture), closed when a code was read or the screen
 * is left. A code read twice in a row is reported once. A QR reader in keyboard mode works
 * without this (the screen's scan field).
 */
export function QrScanner({ onCode }: { onCode: (text: string) => void }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const handler = useRef(onCode);
  const [state, setState] = useState<CameraState>("off");

  useEffect(() => {
    handler.current = onCode;
  });

  const stop = () => {
    for (const track of stream.current?.getTracks() ?? []) {
      track.stop();
    }
    stream.current = null;
    setState((current) => (current === "unavailable" ? current : "off"));
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setState("unavailable");
      return;
    }
    setState("starting");
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      stream.current = media;
      if (video.current) {
        video.current.srcObject = media;
        await video.current.play().catch(() => undefined);
      }
      setState("on");
    } catch {
      setState("unavailable");
    }
  };

  // read frames while the camera is on
  useEffect(() => {
    if (state !== "on") {
      return;
    }
    let stopped = false;
    let last = "";
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    let native: Awaited<ReturnType<typeof nativeQrDetector>> = null;
    void nativeQrDetector().then((detector) => {
      native = detector;
    });
    const tick = async () => {
      const el = video.current;
      if (stopped || !el || el.readyState < 2 || !el.videoWidth) {
        return;
      }
      let text: string | null = null;
      if (native) {
        const hits = await native.detect(el).catch(() => []);
        text = hits[0]?.rawValue ?? null;
      } else if (context) {
        canvas.width = el.videoWidth;
        canvas.height = el.videoHeight;
        context.drawImage(el, 0, 0);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        text = decodeFrame(image);
      }
      if (text && text !== last) {
        last = text;
        handler.current(text);
      }
    };
    const timer = setInterval(() => void tick(), SCAN_EVERY_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [state]);

  // leaving the screen switches the camera off
  useEffect(
    () => () => {
      for (const track of stream.current?.getTracks() ?? []) {
        track.stop();
      }
      stream.current = null;
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-md border bg-muted">
        <video
          ref={video}
          muted
          playsInline
          aria-label={t("pickup.camera_view")}
          data-testid="qr-video"
          className={state === "on" ? "h-full w-full object-cover" : "hidden"}
        />
        {state !== "on" ? (
          <p className="p-4 text-center text-muted-foreground">
            {state === "unavailable"
              ? t("pickup.camera_unavailable")
              : state === "starting"
                ? t("pickup.camera_starting")
                : t("pickup.camera_off")}
          </p>
        ) : null}
      </div>
      {state === "on" ? (
        <button
          type="button"
          onClick={stop}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <CameraOff aria-hidden />
          {t("pickup.camera_stop")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void start()}
          disabled={state === "starting"}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <Camera aria-hidden />
          {t("pickup.camera_start")}
        </button>
      )}
    </div>
  );
}
